import type { Sector } from "api/model";

export type PlotMapLayer =
  "bnd" | "glbl" | "grid" | "lbl" | "orth" | "rlbl" | "row" | "site" | "slbl";
export type ColorTarget = "plots" | "rows";
export type PlotColorBy = "none" | "emerg" | "rc";
export type AnalysisTab = "results" | "runs";
export type ResultsTableView = "plots" | "rows";
export type SidePanelTab = "ovr" | "bnd" | "img" | "plt" | "anl";
export type SidePanelTabOpen = "Y" | "N";

export const ANALYSIS_TAB_PREF_KEY = "analysisTab";
export const COLOR_TARGET_PREF_KEY = "colorTarget";
export const GROWER_PREF_KEY = "grower";
export const LAYERS_PREF_KEY = "layers";
export const MAP_PREFS_STORAGE_KEY = "map";
export const ORTHOS_PREF_KEY = "orthos";
export const PLOT_COLOR_PREF_KEY = "plotColor";
export const RESULTS_TABLE_VIEW_PREF_KEY = "resultsTableView";
/** Last sector the user opened a location in. Private: kept out of the URL. */
export const SECTOR_PREF_KEY = "_sector";
export const SITE_PREF_KEY = "site";
export const SIDE_PANEL_DEFAULT_WIDTH = 600;
export const SIDE_PANEL_MAX_WIDTH = SIDE_PANEL_DEFAULT_WIDTH + 200;
export const SIDE_PANEL_MIN_WIDTH = SIDE_PANEL_DEFAULT_WIDTH - 200;
export const SIDE_PANEL_WIDTH_PREF_KEY = "_sidePanelWidth";
export const TAB_PREF_KEY = "tab";
export const TAB_OPEN_PREF_KEY = "tabopen";
export const TRIAL_PREF_KEY = "trial";

export const defaultMapPrefs = {
  [SECTOR_PREF_KEY]: null as Sector | null,
  [SIDE_PANEL_WIDTH_PREF_KEY]: SIDE_PANEL_DEFAULT_WIDTH as number,
  _url: "map",
  [ANALYSIS_TAB_PREF_KEY]: "runs" as AnalysisTab,
  [COLOR_TARGET_PREF_KEY]: "rows" as ColorTarget,
  [GROWER_PREF_KEY]: null as string | null,
  lat: null,
  [LAYERS_PREF_KEY]: [
    "bnd",
    "glbl",
    "grid",
    "lbl",
    "orth",
    "rlbl",
    "row",
    "site",
    "slbl",
  ] as PlotMapLayer[],
  lng: null,
  location: null,
  [ORTHOS_PREF_KEY]: null as string | null,
  plotColor: "none" as PlotColorBy,
  [RESULTS_TABLE_VIEW_PREF_KEY]: "plots" as ResultsTableView,
  [SITE_PREF_KEY]: null as string | null,
  [TAB_PREF_KEY]: "ovr" as SidePanelTab,
  [TAB_OPEN_PREF_KEY]: "N" as SidePanelTabOpen,
  [TRIAL_PREF_KEY]: null as string | null,
} as const;

export const defaultPrefs: Record<string, Record<string, unknown>> = {
  default: {},
  [MAP_PREFS_STORAGE_KEY]: defaultMapPrefs,
} as const;

/** Merge into stored map prefs so a bare `/map` nav can hydrate them. */
export const writeMapPrefs = (nextPrefs: Record<string, unknown>): void => {
  let stored: Record<string, unknown> = {};
  try {
    stored = JSON.parse(
      localStorage.getItem(MAP_PREFS_STORAGE_KEY) ?? "{}",
    ) as Record<string, unknown>;
  } catch {
    stored = {};
  }

  localStorage.setItem(
    MAP_PREFS_STORAGE_KEY,
    JSON.stringify({
      ...defaultMapPrefs,
      ...stored,
      _url: "map",
      ...nextPrefs,
    }),
  );
};
